/**
 * Trait Event System
 *
 * Every action a student takes on the platform that can affect their trait profile
 * produces a TraitEvent. Events are stored on the student record and replayed to
 * produce the live traitProfile scores.
 *
 * Sources:
 *  - psychometric   Initial assessment (sets baseline, high weight)
 *  - quiz           Micro-course quiz question answered
 *  - course         Completing a full course module
 *  - game           Game session completed
 *  - activity_log   Student self-reported real-world activity (exam result, sport, club, etc.)
 *  - exam_result    Terminal/external exam result entered
 */

export type TraitEventSource =
  | 'psychometric'   // baseline — highest weight, set once
  | 'quiz'           // single quiz question answered
  | 'course'         // full course/module completion bonus
  | 'game'           // game session completed
  | 'activity_log'   // student self-reported activity
  | 'exam_result';   // terminal exam / WAEC / JAMB result

export interface TraitEventMeta {
  /** Time taken in seconds (quiz/game) */
  timeTakenSeconds?: number;
  /** Time limit in seconds for this item */
  timeLimitSeconds?: number;
  /** Raw score achieved (0-100) */
  score?: number;
  /** Difficulty level: 1 (easy) – 3 (hard) */
  difficulty?: 1 | 2 | 3;
  /** Whether the answer was correct (quiz only) */
  correct?: boolean;
  /** Human-readable label e.g. "EquationScape", "Module 1: Introduction to Circuits" */
  label?: string;
  /** Free-form note from student (activity_log) */
  note?: string;
  /** Activity sub-type for activity_log */
  activityType?: ActivityLogType;
  /** Subject/area the activity relates to */
  subject?: string;
}

/** All activity types a student can log */
export type ActivityLogType =
  | 'exam'           // Terminal exam (mid-term, end-of-term)
  | 'test'           // Class test or quiz
  | 'sport'          // Physical / sport activity
  | 'competition'    // Academic or creative competition
  | 'project'        // School project or assignment
  | 'presentation'   // Class presentation or debate
  | 'club'           // Club or society meeting
  | 'workshop'       // Workshop or training
  | 'volunteer'      // Community or volunteer work
  | 'study_session'  // Self-directed study
  | 'other';

export const ACTIVITY_LOG_META: Record<ActivityLogType, {
  label: string;
  icon: string;
  /** Trait IDs most activated by this activity type */
  coreTraits: string[];
  color: string;
}> = {
  exam:          { label: "Exam",           icon: "📝", coreTraits: ["T01","T02","T07","T31","T48"], color: "bg-blue-500/20 text-blue-300 border-blue-400/30" },
  test:          { label: "Class Test",     icon: "📋", coreTraits: ["T01","T47","T31","T48"],       color: "bg-indigo-500/20 text-indigo-300 border-indigo-400/30" },
  sport:         { label: "Sport / PE",     icon: "⚽", coreTraits: ["T25","T44","T41","T48","T28"], color: "bg-emerald-500/20 text-emerald-300 border-emerald-400/30" },
  competition:   { label: "Competition",    icon: "🏆", coreTraits: ["T28","T44","T40","T41","T33"], color: "bg-amber-500/20 text-amber-300 border-amber-400/30" },
  project:       { label: "Project",        icon: "🔧", coreTraits: ["T38","T16","T46","T47","T06"], color: "bg-orange-500/20 text-orange-300 border-orange-400/30" },
  presentation:  { label: "Presentation",   icon: "🎤", coreTraits: ["T17","T28","T22","T18"],       color: "bg-rose-500/20 text-rose-300 border-rose-400/30" },
  club:          { label: "Club / Society", icon: "🤝", coreTraits: ["T18","T19","T33","T23"],       color: "bg-violet-500/20 text-violet-300 border-violet-400/30" },
  workshop:      { label: "Workshop",       icon: "🛠️", coreTraits: ["T04","T12","T11","T46"],       color: "bg-cyan-500/20 text-cyan-300 border-cyan-400/30" },
  volunteer:     { label: "Volunteer",      icon: "💚", coreTraits: ["T14","T19","T42","T30"],       color: "bg-teal-500/20 text-teal-300 border-teal-400/30" },
  study_session: { label: "Study Session",  icon: "📚", coreTraits: ["T31","T48","T11","T12"],       color: "bg-slate-500/20 text-slate-300 border-slate-400/30" },
  other:         { label: "Other",          icon: "✨", coreTraits: ["T46","T48"],                   color: "bg-pink-500/20 text-pink-300 border-pink-400/30" },
};

/** How the student felt it went (1-5 scale used for outcome delta multiplier) */
export type OutcomeRating = 1 | 2 | 3 | 4 | 5;

export const OUTCOME_META: Record<OutcomeRating, { label: string; emoji: string; multiplier: number }> = {
  1: { label: "Really struggled",  emoji: "😤", multiplier: 0.3 },
  2: { label: "It was tough",      emoji: "😟", multiplier: 0.6 },
  3: { label: "It was okay",       emoji: "😐", multiplier: 0.85 },
  4: { label: "Went well",         emoji: "😊", multiplier: 1.0 },
  5: { label: "Crushed it!",       emoji: "🤩", multiplier: 1.3 },
};

export interface TraitEvent {
  id: string;
  studentId: string;
  source: TraitEventSource;
  sourceId: string;          // courseId, gameId, activityId, "psychometric"
  sourceName: string;        // human-readable
  /** Raw trait deltas — will be modified by mutation engine before application */
  traitDeltas: Record<string, number>;   // traitId → raw delta (-10 to +10)
  timestamp: string;         // ISO string (Date serialised for storage)
  meta: TraitEventMeta;
}

/** Activity log entry (stored separately on student record, also creates a TraitEvent) */
export interface ActivityLogEntry {
  id: string;
  activityType: ActivityLogType;
  subject: string;
  outcomeRating: OutcomeRating;
  score?: number;            // 0-100 if student knows the actual score
  note?: string;
  timestamp: string;
  traitEventId: string;      // links back to the TraitEvent it produced
}

/** Game result summary passed to useTraitMutation */
export interface GameResult {
  gameId: string;
  gameName: string;
  score: number;             // raw game score
  maxScore: number;          // max achievable score
  durationSeconds: number;
  difficulty: 1 | 2 | 3;
  /** Trait IDs this game primarily exercises */
  primaryTraits: string[];
}

/** Quiz question result passed to useTraitMutation */
export interface QuizResult {
  questionId: string;
  courseId: string;
  courseName: string;
  correct: boolean;
  timeTakenSeconds: number;
  timeLimitSeconds: number;
  /** Deltas defined by the question author, before engine modifiers */
  rawTraitDeltas: Record<string, number>;
}
